import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'tokengate_auth';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')?.token || null; } catch { return null; }
  });
  const [loading, setLoading] = useState(!!token);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [googleClientId, setGoogleClientId] = useState('');

  // Global axios auth header
  useEffect(() => {
    if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    else delete axios.defaults.headers.common['Authorization'];
  }, [token]);

  // Restore user from server if token exists
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setLoading(false); return; }
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        if (!cancelled) { setUser(data); }
      } catch (e) {
        if (!cancelled) { setToken(null); localStorage.removeItem(STORAGE_KEY); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  // Query Google availability
  useEffect(() => {
    axios.get(`${API}/auth/google/status`).then(({ data }) => {
      setGoogleEnabled(!!data.enabled);
      setGoogleClientId(data.client_id || '');
    }).catch(() => {});
  }, []);

  const persist = (tok, usr) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ token: tok, user: usr }));
    setToken(tok);
    setUser(usr);
  };

  const signupRequest = useCallback(async (email, password, name, referralCode) => {
    const payload = { email, password, name };
    if (referralCode) payload.referral_code = referralCode;
    const { data } = await axios.post(`${API}/auth/signup`, payload);
    return data; // { pending_id, email, otp_expires_in, resend_available_in }
  }, []);

  const signupVerify = useCallback(async (pendingId, otp) => {
    const { data } = await axios.post(`${API}/auth/signup/verify`, { pending_id: pendingId, otp });
    persist(data.access_token, data.user);
    return data.user;
  }, []);

  const signupResend = useCallback(async (pendingId) => {
    const { data } = await axios.post(`${API}/auth/signup/resend`, { pending_id: pendingId });
    return data;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    persist(data.access_token, data.user);
    return data.user;
  }, []);

  const loginWithGoogle = useCallback(async (idToken, referralCode) => {
    const payload = { id_token: idToken };
    if (referralCode) payload.referral_code = referralCode;
    const { data } = await axios.post(`${API}/auth/google`, payload);
    persist(data.access_token, data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, googleEnabled, googleClientId, signupRequest, signupVerify, signupResend, login, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
